import { useState } from 'react';
import {
  Box,
  Typography,
  Button,
  IconButton,
  Chip,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  TextField,
  MenuItem,
  FormControlLabel,
  Switch,
  CircularProgress,
  Tooltip,
  useTheme,
  Snackbar,
  Alert,
} from '@mui/material';
import {
  Add as AddIcon,
  Refresh as RefreshIcon,
} from '@mui/icons-material';
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { couponService, type Coupon, type CreateCouponData, type DiscountType } from '../../../shared/services/coupon/couponService';
import { eventService } from '../../../shared/services/tenant/eventService';
import { DatabaseTable } from '../../../shared/components/ui/DatabaseTable';
import { format } from 'date-fns';
import { ptBR } from 'date-fns/locale';

const initialFormData: Partial<CreateCouponData> = {
  code: '',
  event_id: '',
  discount_type: 'fixed', // Padrão: valor fixo
  discount_value: 0,
  max_uses: undefined,
  valid_from: '',
  valid_until: '',
  is_active: true,
  description: '',
};

export default function CouponsPage() {
  const theme = useTheme();
  const queryClient = useQueryClient();
  const [dialogOpen, setDialogOpen] = useState(false);
  const [deleteDialogOpen, setDeleteDialogOpen] = useState(false);
  const [selectedCoupon, setSelectedCoupon] = useState<Coupon | null>(null);
  const [snackbar, setSnackbar] = useState<{ open: boolean; message: string; severity: 'success' | 'error' }>({
    open: false,
    message: '',
    severity: 'success',
  });

  const showToast = (message: string, severity: 'success' | 'error') => {
    setSnackbar({ open: true, message, severity });
  };
  const [formData, setFormData] = useState<Partial<CreateCouponData>>(initialFormData);
  const [page, setPage] = useState(1);
  const [limit, setLimit] = useState(15);

  const { data: couponsResponse, isLoading } = useQuery({
    queryKey: ['coupons', page, limit],
    queryFn: () => couponService.list(page, limit).then(res => res.data),
  });

  const couponsData = couponsResponse?.data || [];
  const total = couponsResponse?.total || 0;

  const handlePageChange = (newPage: number) => {
    setPage(newPage);
  };

  const handleLimitChange = (newLimit: number) => {
    setLimit(newLimit);
    setPage(1);
  };

  // Buscar eventos para o select
  const { data: eventsData } = useQuery({
    queryKey: ['events-list'],
    queryFn: () => eventService.list(1, 100).then(res => res.data.data),
  });

  // Mutations
  const createMutation = useMutation({
    mutationFn: (data: CreateCouponData) => couponService.create(data),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['coupons'] });
      showToast('Cupom criado com sucesso!', 'success');
      handleCloseDialog();
    },
    onError: (error: any) => {
      showToast(error?.response?.data?.message || 'Erro ao criar cupom', 'error');
    },
  });

  const updateMutation = useMutation({
    mutationFn: ({ id, data }: { id: string; data: Partial<CreateCouponData> }) => 
      couponService.update(id, data),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['coupons'] });
      showToast('Cupom atualizado com sucesso!', 'success');
      handleCloseDialog();
    },
    onError: (error: any) => {
      showToast(error?.response?.data?.message || 'Erro ao atualizar cupom', 'error');
    },
  });

  const deleteMutation = useMutation({
    mutationFn: (id: string) => couponService.delete(id),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['coupons'] });
      showToast('Cupom excluído com sucesso!', 'success');
      setDeleteDialogOpen(false);
      setSelectedCoupon(null);
    },
    onError: (error: any) => {
      showToast(error?.response?.data?.message || 'Erro ao excluir cupom', 'error');
    },
  });

  const handleOpenDialog = (coupon?: Coupon) => {
    if (coupon) {
      setSelectedCoupon(coupon);
      setFormData({
        code: coupon.code,
        event_id: coupon.event_id,
        discount_type: coupon.discount_type,
        discount_value: Number(coupon.discount_value),
        max_uses: coupon.max_uses,
        valid_from: coupon.valid_from ? coupon.valid_from.split('T')[0] : '',
        valid_until: coupon.valid_until ? coupon.valid_until.split('T')[0] : '',
        is_active: coupon.is_active,
        description: coupon.description || '',
      });
    } else {
      setSelectedCoupon(null);
      setFormData(initialFormData);
    }
    setDialogOpen(true);
  };

  const handleCloseDialog = () => {
    setDialogOpen(false);
    setSelectedCoupon(null);
    setFormData(initialFormData);
  };

  const handleSubmit = () => {
    if (!formData.code || !formData.event_id || formData.discount_value === undefined) {
      showToast('Preencha todos os campos obrigatórios', 'error');
      return;
    }

    const data: CreateCouponData = {
      code: formData.code!,
      event_id: formData.event_id!,
      discount_type: formData.discount_type as DiscountType,
      discount_value: Number(formData.discount_value),
      max_uses: formData.max_uses ? Number(formData.max_uses) : undefined,
      valid_from: formData.valid_from || undefined,
      valid_until: formData.valid_until || undefined,
      is_active: formData.is_active ?? true,
      description: formData.description || undefined,
    };

    if (selectedCoupon) {
      updateMutation.mutate({ id: selectedCoupon.id, data });
    } else {
      createMutation.mutate(data);
    }
  };

  const formatDiscount = (coupon: Coupon) => {
    if (coupon.discount_type === 'percentage') {
      return `${coupon.discount_value}%`;
    }
    return `R$ ${Number(coupon.discount_value).toFixed(2).replace('.', ',')}`;
  };

  const formatDate = (dateStr?: string) => {
    if (!dateStr) return '-';
    return format(new Date(dateStr + 'T00:00:00'), 'dd/MM/yyyy', { locale: ptBR });
  };

  // Colunas para a tabela
  const columns = [
    { 
      field: 'code', 
      headerName: 'Código', 
      flex: 1, 
      minWidth: 120,
      renderCell: (params: any) => (
        <Typography fontWeight={600} fontFamily="monospace" fontSize={14}>
          {params.value}
        </Typography>
      )
    },
    { 
      field: 'event', 
      headerName: 'Evento', 
      flex: 1.5, 
      minWidth: 200,
      renderCell: (params: any) => (
        <Typography fontSize={14}>
          {params.row.event?.title || '-'}
        </Typography>
      )
    },
    { 
      field: 'discount_value', 
      headerName: 'Desconto', 
      width: 120,
      renderCell: (params: any) => (
        <Chip 
          label={formatDiscount(params.row)} 
          color="primary" 
          size="small"
        />
      )
    },
    { 
      field: 'current_uses', 
      headerName: 'Usos', 
      width: 100,
      renderCell: (params: any) => (
        <Typography fontSize={14}>
          {params.value} / {params.row.max_uses || '∞'}
        </Typography>
      )
    },
    { 
      field: 'valid_until', 
      headerName: 'Validade', 
      width: 180,
      renderCell: (params: any) => (
        <Typography variant="caption" fontSize={13}>
          {formatDate(params.row.valid_from)} - {formatDate(params.row.valid_until)}
        </Typography>
      )
    },
    { 
      field: 'is_active', 
      headerName: 'Status', 
      width: 100,
      renderCell: (params: any) => (
        <Chip
          label={params.value ? 'Ativo' : 'Inativo'}
          color={params.value ? 'success' : 'default'}
          size="small"
        />
      )
    },
  ];

  const handleEdit = (row: Coupon) => {
    handleOpenDialog(row);
  };

  const handleDelete = (row: Coupon) => {
    setSelectedCoupon(row);
    setDeleteDialogOpen(true);
  };

  return (
    <>
      {/* Snackbar de feedback */}
      <Snackbar 
        open={snackbar.open} 
        autoHideDuration={4000} 
        onClose={() => setSnackbar({ ...snackbar, open: false })}
        anchorOrigin={{ vertical: 'top', horizontal: 'right' }}
      >
        <Alert severity={snackbar.severity} onClose={() => setSnackbar({ ...snackbar, open: false })}>
          {snackbar.message}
        </Alert>
      </Snackbar>

      <Box style={{ 
        background: theme.palette.background.paper, 
        borderRadius: 16, 
        padding: 32, 
        boxShadow: '0 2px 8px 0 rgba(16,30,54,0.04)', 
        maxWidth: '100%', 
        margin: '0 auto' 
      }}>
        {/* Header */}
        <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 3 }}>
          <Typography variant="h5" fontWeight={700} color={theme.palette.text.primary}>
            Cupons de Desconto
          </Typography>
          <Box sx={{ display: 'flex', gap: 1 }}>
            <Tooltip title="Atualizar">
              <IconButton onClick={() => queryClient.invalidateQueries({ queryKey: ['coupons'] })}>
                <RefreshIcon />
              </IconButton>
            </Tooltip>
            <Button
              variant="contained"
              startIcon={<AddIcon />}
              onClick={() => handleOpenDialog()}
            >
              Novo Cupom
            </Button>
          </Box>
        </Box>

        {/* Tabela */}
        <DatabaseTable
          rows={couponsData}
          columns={columns}
          loading={isLoading}
          onEdit={handleEdit}
          onDelete={handleDelete}
          hideView
          hidePdf
          paginationMode="server"
          page={page}
          pageSizeOptions={[5, 15, 25, 50, 100]}
          pageSize={limit}
          rowCount={total}
          onPageChange={handlePageChange}
          onPageSizeChange={handleLimitChange}
        />
      </Box>

      {/* Dialog de criar/editar */}
      <Dialog open={dialogOpen} onClose={handleCloseDialog} maxWidth="sm" fullWidth>
        <DialogTitle>
          {selectedCoupon ? 'Editar Cupom' : 'Novo Cupom'}
        </DialogTitle>
        <DialogContent>
          <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2, mt: 1 }}>
            <TextField
              label="Código do Cupom"
              value={formData.code}
              onChange={(e) => setFormData({ ...formData, code: e.target.value.toUpperCase() })}
              required
              fullWidth
              placeholder="Ex: NATAL2025"
              inputProps={{ style: { textTransform: 'uppercase' } }}
            />

            <TextField
              select
              label="Evento"
              value={formData.event_id}
              onChange={(e) => setFormData({ ...formData, event_id: e.target.value })}
              required
              fullWidth
            >
              {eventsData?.map((event: any) => (
                <MenuItem key={event.id} value={event.id}>
                  {event.title}
                </MenuItem>
              ))}
            </TextField>

            <Box sx={{ display: 'flex', gap: 2 }}>
              <TextField
                select
                label="Tipo de Desconto"
                value={formData.discount_type}
                onChange={(e) => setFormData({ ...formData, discount_type: e.target.value as DiscountType })}
                required
                sx={{ flex: 1 }}
              >
                <MenuItem value="fixed">Valor Fixo (R$)</MenuItem>
                <MenuItem value="percentage">Percentual (%)</MenuItem>
              </TextField>

              <TextField
                label={formData.discount_type === 'percentage' ? 'Valor do Desconto (%)' : 'Valor do Desconto (R$)'}
                type="number"
                value={formData.discount_value}
                onChange={(e) => setFormData({ ...formData, discount_value: Number(e.target.value) })}
                required
                sx={{ flex: 1 }}
                inputProps={{ min: 0, max: formData.discount_type === 'percentage' ? 100 : undefined, step: formData.discount_type === 'percentage' ? 0.01 : 0.01 }}
                helperText={formData.discount_type === 'percentage' ? 'Máximo de 100%' : undefined}
              />
            </Box>

            <TextField
              label="Limite de Usos"
              type="number"
              value={formData.max_uses || ''}
              onChange={(e) => setFormData({ ...formData, max_uses: e.target.value ? Number(e.target.value) : undefined })}
              fullWidth
              placeholder="Deixe vazio para ilimitado"
              inputProps={{ min: 1 }}
            />

            <Box sx={{ display: 'flex', gap: 2 }}>
              <TextField
                label="Válido a partir de"
                type="date"
                value={formData.valid_from}
                onChange={(e) => setFormData({ ...formData, valid_from: e.target.value })}
                sx={{ flex: 1 }}
                InputLabelProps={{ shrink: true }}
              />

              <TextField
                label="Válido até"
                type="date"
                value={formData.valid_until}
                onChange={(e) => setFormData({ ...formData, valid_until: e.target.value })}
                sx={{ flex: 1 }}
                InputLabelProps={{ shrink: true }}
              />
            </Box>

            <TextField
              label="Descrição (opcional)"
              value={formData.description}
              onChange={(e) => setFormData({ ...formData, description: e.target.value })}
              fullWidth
              multiline
              rows={2}
              placeholder="Descrição interna do cupom"
            />

            <FormControlLabel
              control={
                <Switch
                  checked={formData.is_active ?? true}
                  onChange={(e) => setFormData({ ...formData, is_active: e.target.checked })}
                />
              }
              label="Cupom ativo"
            />
          </Box>
        </DialogContent>
        <DialogActions>
          <Button onClick={handleCloseDialog}>Cancelar</Button>
          <Button 
            onClick={handleSubmit} 
            variant="contained"
            disabled={createMutation.isPending || updateMutation.isPending}
          >
            {(createMutation.isPending || updateMutation.isPending) ? (
              <CircularProgress size={20} />
            ) : selectedCoupon ? 'Salvar' : 'Criar'}
          </Button>
        </DialogActions>
      </Dialog>

      {/* Dialog de confirmação de exclusão */}
      <Dialog open={deleteDialogOpen} onClose={() => setDeleteDialogOpen(false)}>
        <DialogTitle>Confirmar exclusão</DialogTitle>
        <DialogContent>
          <Typography>
            Tem certeza que deseja excluir o cupom <strong>{selectedCoupon?.code}</strong>?
          </Typography>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setDeleteDialogOpen(false)}>Cancelar</Button>
          <Button 
            onClick={() => selectedCoupon && deleteMutation.mutate(selectedCoupon.id)} 
            color="error"
            variant="contained"
            disabled={deleteMutation.isPending}
          >
            {deleteMutation.isPending ? <CircularProgress size={20} /> : 'Excluir'}
          </Button>
        </DialogActions>
      </Dialog>
    </>
  );
}

